import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Geo-blokkade: veelvoorkomende spam-landen krijgen geen toegang tot de
// website. Cloudflare zet de landcode van de bezoeker in de cf-ipcountry
// header; ontbreekt die (lokale dev), dan laten we het verzoek door.
const BLOCKED_COUNTRIES = new Set(["IN", "PK", "BD", "NG", "RU", "CN"]);

const geoBlockMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  const country = request.headers.get("cf-ipcountry")?.toUpperCase();
  if (country && BLOCKED_COUNTRIES.has(country)) {
    return new Response(
      "<!doctype html><html lang=\"nl\"><head><meta charset=\"utf-8\"><title>Geen toegang</title></head><body><h1>Geen toegang</h1><p>Deze website is niet beschikbaar vanuit jouw regio.</p></body></html>",
      { status: 403, headers: { "content-type": "text/html; charset=utf-8" } },
    );
  }
  return next();
});

const CITY_PAGE_RE = /^\/groepenkast-vervangen\/([a-z0-9-]+)$/i;

async function logCityHit(params: {
  stad: string;
  host: string;
  path: string;
  status: number;
  userAgent: string | null;
  referrer: string | null;
}) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("city_page_hits").insert({
      stad: params.stad.toLowerCase(),
      host: params.host,
      path: params.path,
      status: params.status,
      user_agent: params.userAgent,
      referrer: params.referrer,
    });
  } catch (err) {
    console.error("[city-hit-log]", err);
  }
}

// Host-canonicalisatie: dwing non-www af zodat Google altijd de
// https://elvion-group.nl/... variant indexeert. 301 op edge-niveau
// zorgt dat crawlers zowel de canonical-tag als de redirect zien.
const canonicalHostMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  const host = url.hostname.toLowerCase();
  const isWww = host === "www.elvion-group.nl";
  const cityMatch = url.pathname.match(CITY_PAGE_RE);

  if (isWww) {
    if (cityMatch) {
      await logCityHit({
        stad: cityMatch[1],
        host,
        path: url.pathname,
        status: 301,
        userAgent: request.headers.get("user-agent"),
        referrer: request.headers.get("referer"),
      });
    }
    const target = `https://elvion-group.nl${url.pathname}${url.search}`;
    return new Response(null, {
      status: 301,
      headers: { location: target, "cache-control": "public, max-age=3600" },
    });
  }

  if (cityMatch) {
    await logCityHit({
      stad: cityMatch[1],
      host,
      path: url.pathname,
      status: 200,
      userAgent: request.headers.get("user-agent"),
      referrer: request.headers.get("referer"),
    });
  }

  return next();
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [geoBlockMiddleware, canonicalHostMiddleware, errorMiddleware],
}));


